/** @file Cubic bezier for timeline segments: solves x(s)=u by Newton-Raphson with a bisection fallback. */

const EPS = 1e-7;
const MAX_NEWTON = 8;
const BISECT_STEPS = 40;

/**
 * Parameter s in [0,1] with x(s) = u for x(s) = ((ax*s + bx)*s + cx)*s: Newton steps from s = u until |x(s) - u| < EPS,
 * then (on a flat derivative, a step out of [0,1] or no convergence) BISECT_STEPS halvings. Plain arithmetic on
 * numbers, no closures: a closure here is two heap allocations per call whenever the bundler keeps function names.
 */
function solveParameter(ax: number, bx: number, cx: number, u: number): number {
  let s = u;
  for (let i = 0; i < MAX_NEWTON; i++) {
    const err = ((ax * s + bx) * s + cx) * s - u;
    if (Math.abs(err) < EPS) return s;
    const d = (3 * ax * s + 2 * bx) * s + cx;
    if (Math.abs(d) < 1e-6) break;
    s -= err / d;
    if (s < 0 || s > 1) break;
  }
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < BISECT_STEPS; i++) {
    s = (lo + hi) / 2;
    if (((ax * s + bx) * s + cx) * s < u) lo = s;
    else hi = s;
  }
  return s;
}

/**
 * Evaluates a timeline segment curve. x1,x2 are normalized control times in [0,1] (monotone x guaranteed);
 * cy1,cy2 are absolute control values; v0,v1 the segment's start/end values; u the normalized time in [0,1].
 * Allocation-free.
 */
export function evalCubicBezier(x1: number, cy1: number, x2: number, cy2: number, v0: number, v1: number, u: number): number {
  if (u <= 0) return v0;
  if (u >= 1) return v1;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const s = solveParameter(ax, bx, cx, u);
  const t = 1 - s;
  return t * t * t * v0 + 3 * t * t * s * cy1 + 3 * t * s * s * cy2 + s * s * s * v1;
}
